import { useState } from "react";
import {
  Alert, Box, Button, Chip, MenuItem, Paper, Stack, TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { DataGrid, type GridColDef, type GridPaginationModel } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { purchaseInvoicesApi, type PurchaseInvoiceListDto } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { useFeatureFlags } from "@/store/featureFlags";
import { PageHeader } from "@/components/PageHeader";
import { StatusPill } from "@/components/StatusPill";
import { formatDate } from "@/shared/datetime";

const inr = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 });
const STATUS = ["", "Draft", "Submitted", "Approved", "Posted", "Cancelled"];

/**
 * UC-M1-PROC-05 — Purchase invoice list. One PI per posted GRN (enforced server-side).
 * Posted PIs link to a balanced JournalEntry (DR Inventory 1300 + DR GST 1410 / CR AP 2100).
 */
export function PurchaseInvoicesPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("");
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const canCreate = useAuth(s => s.hasPermission("procurement.pi.create"));
  const hasDirectPerm = useAuth(s => s.hasPermission("procurement.pi.create-direct"));
  const { isEnabled } = useFeatureFlags();
  // Direct PI is a feature-flagged (MVP2) capability. Only surface the entry point when the
  // tenant flag is on — otherwise the endpoint returns 403 FEATURE_NOT_ENABLED. Enable it in
  // Admin → Feature Flags.
  const canCreateDirect = hasDirectPerm && isEnabled("procurement.direct-pi");

  const query = useQuery({
    queryKey: ["purchase-invoices", status, pagination.page, pagination.pageSize],
    queryFn: () => purchaseInvoicesApi.list({
      status: status || undefined,
      page: pagination.page + 1, pageSize: pagination.pageSize,
    }),
  });

  const columns: GridColDef<PurchaseInvoiceListDto>[] = [
    { field: "documentNo", headerName: "PI #", width: 160,
      renderCell: (p) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <span>{p.value as string}</span>
          {p.row.source === "Direct" && (
            <Chip size="small" color="warning" variant="outlined" label="Direct" />
          )}
        </Stack>
      ) },
    { field: "documentDate", headerName: "Date", width: 110,
      valueFormatter: (v) => v ? formatDate(v as string) : "" },
    { field: "vendorName", headerName: "Vendor", flex: 1, minWidth: 200 },
    { field: "grnNo", headerName: "Linked GRN", width: 140,
      valueGetter: (_v, row) => row.grnNo ?? "—" },
    { field: "vendorInvoiceNo", headerName: "Vendor invoice", width: 160,
      valueGetter: (_v, row) => row.vendorInvoiceNo ?? "—" },
    { field: "status", headerName: "Status", width: 130,
      renderCell: (p) => <StatusPill label={p.value as string} /> },
    { field: "totalAmount", headerName: "Total", width: 140, type: "number",
      valueFormatter: (v) => inr.format(Number(v ?? 0)) },
  ];

  return (
    <Box>
      <PageHeader
        breadcrumbs={["Procurement", "Purchase invoices"]}
        title="Purchase invoices"
        subtitle="One PI per posted GRN; posting books the AP journal entry."
        actions={
          <>
            {canCreateDirect && (
              <Button variant="outlined" color="warning" startIcon={<AddIcon />}
                onClick={() => navigate("/procurement/purchase-invoices/direct")}>
                New direct PI
              </Button>
            )}
            {canCreate && (
              <Button variant="contained" startIcon={<AddIcon />}
                onClick={() => navigate("/procurement/purchase-invoices/new")}>
                New PI
              </Button>
            )}
          </>
        }
      />
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <TextField select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}
            size="small" sx={{ minWidth: 160 }}>
            {STATUS.map((s) => <MenuItem key={s} value={s}>{s || "All"}</MenuItem>)}
          </TextField>
        </Stack>
        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        <Box sx={{ height: 600 }}>
          <DataGrid
            rows={query.data?.items ?? []}
            columns={columns}
            loading={query.isLoading}
            paginationMode="server"
            rowCount={query.data?.totalCount ?? 0}
            paginationModel={pagination}
            onPaginationModelChange={setPagination}
            pageSizeOptions={[10, 25, 50, 100]}
            disableRowSelectionOnClick
            getRowId={(row) => row.id}
            onRowClick={(p) => navigate(`/procurement/purchase-invoices/${p.id}`)}
            sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
          />
        </Box>
      </Paper>
    </Box>
  );
}
